import { motion } from "framer-motion";
import { BarChart3, LineChart, PieChart, TrendingUp, Users, DollarSign, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const revenueData = [
  { month: 'Jan', revenue: 65000 },
  { month: 'Feb', revenue: 45000 },
  { month: 'Mar', revenue: 75000 },
  { month: 'Apr', revenue: 55000 },
  { month: 'May', revenue: 60000 },
  { month: 'Jun', revenue: 80000 },
  { month: 'Jul', revenue: 70000 },
  { month: 'Aug', revenue: 85000 },
  { month: 'Sep', revenue: 75000 },
  { month: 'Oct', revenue: 80000 },
  { month: 'Nov', revenue: 90000 },
  { month: 'Dec', revenue: 85000 }
];

export const DashboardPreview = () => {
  return (
    <section className="py-20 bg-gradient-to-b from-blue-50 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Interactive Analytics Dashboard
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get actionable insights with our comprehensive analytics dashboard, inspired by industry-leading tools like Tableau.
          </p>
        </div>
        
        <motion.div 
          initial={{ y: 100, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="relative rounded-xl shadow-2xl bg-white"
        >
          <div className="p-6 bg-white rounded-xl border">
            {/* Dashboard Header */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-600">Total Revenue</p>
                    <h3 className="text-2xl font-bold text-gray-900">$124.5K</h3>
                    <p className="text-sm text-green-600 flex items-center">
                      <ArrowUpRight className="w-4 h-4 mr-1" />
                      +12.5%
                    </p>
                  </div>
                  <DollarSign className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-600">Active Users</p>
                    <h3 className="text-2xl font-bold text-gray-900">2,453</h3>
                    <p className="text-sm text-green-600 flex items-center">
                      <ArrowUpRight className="w-4 h-4 mr-1" />
                      +8.2%
                    </p>
                  </div>
                  <Users className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-600">Conversion Rate</p>
                    <h3 className="text-2xl font-bold text-gray-900">3.8%</h3>
                    <p className="text-sm text-red-600 flex items-center">
                      <ArrowDownRight className="w-4 h-4 mr-1" />
                      -2.1%
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-purple-600" />
                </div>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-600">Avg. Order Value</p>
                    <h3 className="text-2xl font-bold text-gray-900">$85.20</h3>
                    <p className="text-sm text-green-600 flex items-center">
                      <ArrowUpRight className="w-4 h-4 mr-1" />
                      +5.4%
                    </p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-orange-600" />
                </div>
              </div>
            </div>

            {/* Main Dashboard Content */}
            <div className="grid grid-cols-12 gap-6">
              {/* Revenue Chart */}
              <div className="col-span-8 bg-gray-50 p-6 rounded-lg">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold text-gray-900">Revenue Overview</h4>
                  <select className="text-sm border rounded px-2 py-1">
                    <option>Last 30 days</option>
                    <option>Last 90 days</option>
                    <option>Last year</option>
                  </select>
                </div>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={revenueData}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis 
                        dataKey="month" 
                        stroke="#6B7280"
                        fontSize={12}
                        tickLine={false}
                      />
                      <YAxis 
                        stroke="#6B7280"
                        fontSize={12}
                        tickLine={false}
                        tickFormatter={(value) => `$${value/1000}k`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white',
                          border: '1px solid #E5E7EB',
                          borderRadius: '0.5rem',
                          padding: '0.5rem'
                        }}
                        formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="revenue" 
                        stroke="#3B82F6" 
                        fillOpacity={1} 
                        fill="url(#colorRevenue)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Side Charts */}
              <div className="col-span-4 space-y-6">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-4">Traffic Sources</h4>
                  <div className="space-y-4">
                    {[
                      { label: "Organic Search", value: 45, color: "bg-blue-500" },
                      { label: "Direct", value: 30, color: "bg-green-500" },
                      { label: "Social", value: 15, color: "bg-purple-500" },
                      { label: "Referral", value: 10, color: "bg-orange-500" },
                    ].map((source) => (
                      <div key={source.label}>
                        <div className="flex justify-between text-sm mb-1">
                          <span>{source.label}</span>
                          <span>{source.value}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded">
                          <div 
                            className={`h-full rounded ${source.color}`}
                            style={{ width: `${source.value}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};